const BASE_URL = import.meta.env.VITE_STUDIO_API_BASE_URL || 'http://localhost:8087';

let getAccessToken: (() => Promise<string>) | null = null;

export function setAccessTokenProvider(provider: () => Promise<string>) {
  getAccessToken = provider;
}

async function authHeaders(includeContentType = true): Promise<HeadersInit> {
  const headers: HeadersInit = {};
  if (includeContentType) headers['Content-Type'] = 'application/json';
  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      console.error('Failed to get access token:', error);
    }
  }
  return headers;
}

export class APIError extends Error {
  status: number;
  body?: Record<string, unknown>;

  constructor(status: number, statusText: string, body?: Record<string, unknown>) {
    const error = typeof body?.error === 'string' ? body.error : undefined;
    super(error || `API error: ${statusText}`);
    this.name = 'APIError';
    this.status = status;
    this.body = body;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorBody: Record<string, unknown> | undefined;
    try {
      errorBody = (await response.json()) as Record<string, unknown>;
    } catch {
      // non-JSON
    }
    throw new APIError(response.status, response.statusText, errorBody);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Any organization in the shared WorkOS environment, Studio's or not (`GET /api/admin/workos-organizations`). */
export type AdminWorkOSOrganization = {
  workosId: string;
  /** The id Studio keys the organization by. */
  organizationId: string;
  name: string;
  domains: string[];
  /** Marked as Studio's (`metadata.studio`). */
  studio: boolean;
};

/** What happened to the first owner: a seat now, or an invitation that carries it. */
export type AdminFirstOwner = { kind: 'seat' | 'invitation'; email: string };

export type AdminOrganizationRow = {
  organizationId: string;
  name: string;
  displayName: string;
  projectCount: number;
  /**
   * The last 30 days. `costUsd` is AI building only; `totalCostUsd` adds integrations, the
   * figure the organization's Money page shows.
   */
  usage30d: { costUsd: number; runs: number; integrationCostUsd?: number; totalCostUsd?: number };
  billing?: {
    status?: string;
    productName?: string;
    stripeCustomerId?: string;
  };
  /** The org's Fabric cluster and namespace, once placed. */
  fabric?: {
    fabricId: string;
    namespace?: string;
    state?: AdminFabricPlacementState;
  };
  /** Active Studio seat or a pending grant. Organization membership alone is not access. */
  studioAccess: boolean;
};

export type AdminProjectPublishHint = {
  publishId: string;
  status?: string;
  environment?: string;
  realmId?: string;
  appSlug?: string;
  displayName?: string;
  shellUrl?: string;
  hosting?: string;
  ready?: boolean;
  gatewayUrl?: string;
};

export type AdminProjectState = 'active' | 'deleted' | 'all';

export type AdminProjectRow = {
  projectId: string;
  title: string;
  displayName?: string;
  packageName?: string;
  status: string;
  ownerUserId: string;
  workspaceKind?: string;
  environment?: string;
  currentSnapshotId?: string;
  latestSessionId?: string;
  latestSessionSnapshotId?: string;
  latestSessionTitle?: string;
  latestPublish?: AdminProjectPublishHint;
  publishedVersionCount: number;
  updatedAt: string;
  createdAt: string;
};

export type AdminUsageTotals = {
  costUsd: number;
  llmCostUsd?: number;
  integrationCostUsd?: number;
  runs: number;
  inputTokens: number;
  outputTokens: number;
  activeUsers: number;
};

export type AdminUsageSeriesPoint = {
  date: string;
  costUsd: number;
  llmCostUsd?: number;
  integrationCostUsd?: number;
  runs: number;
};

export type AdminUsageByUserRow = {
  userId: string;
  displayName: string;
  email?: string;
  picture?: string;
  runs: number;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
};

export type AdminUsageSummary = {
  from: string;
  to: string;
  totals: AdminUsageTotals;
  series: AdminUsageSeriesPoint[];
  byUser: AdminUsageByUserRow[];
};

export type AdminUsageEventKind = 'llm' | 'integration';

export type AdminUsageEventRow = {
  kind: AdminUsageEventKind;
  runId: string;
  sessionId: string;
  title: string;
  userId: string;
  displayName: string;
  email?: string;
  createdAt: string;
  status: string;
  promptSnippet: string;
  usage: {
    costUsd?: number;
    turns?: number;
    inputTokens?: number;
    outputTokens?: number;
  };
  integrationId?: string;
  vendor?: string;
  action?: string;
  feedback?: {
    upCount: number;
    downCount: number;
    votes: Array<{
      userId: string;
      displayName: string;
      email?: string;
      rating: 'up' | 'down';
      runId: string;
      updatedAt: string;
    }>;
  };
};

export type AdminUsageEventsResponse = {
  from: string;
  to: string;
  events: AdminUsageEventRow[];
  nextCursor?: string;
};

export type AdminFabric = {
  id: string;
  displayName: string;
  environment: string;
  provider: string;
  account: string;
  region: string;
  stack: string;
  apiUrl: string;
  appsDomain: string;
  default: boolean;
  acceptingOrgs: boolean;
};

export type AdminFabricPlacementState = 'pending' | 'provisioning' | 'ready' | 'failed';

export type AdminFabricRow = AdminFabric & {
  organizations: Record<AdminFabricPlacementState, number>;
};

export type AdminFabricCapacityLevel = 'ok' | 'warning' | 'full';

export type AdminFabricCapacityRow = {
  fabricId: string;
  displayName: string;
  region: string;
  default: boolean;
  acceptingOrgs: boolean;
  dedicatedOrgIds: string[];
  namespaceCapacity: number;
  capacityWarningPercent: number;
  warningAt: number;
  /** Counted from Studio's records; placement decides with these. */
  used: { namespaces: number; organizations: number; apps: number };
  usedPercent: number;
  level: AdminFabricCapacityLevel;
  /** What the cluster's platform API lists. */
  platform: { state: 'ok'; namespaces: number; readAt: string } | { state: 'unreachable'; reason: string; readAt: string };
  /** Platform count minus Studio's; null when the cluster did not answer. */
  drift: number | null;
};

export type AdminFabricCapacity = {
  environment: string;
  refreshedAt: string;
  items: AdminFabricCapacityRow[];
};

export type AdminOrgFabricPlacement = {
  organizationId: string;
  fabricId: string;
  namespace: string;
  state: AdminFabricPlacementState;
  error?: string;
  attempts: number;
  operationId?: string;
  readyAt?: string;
  updatedAt?: string;
};

/** One Builder connector's org settings and this org's values (proposal 0204). */
export type AdminOrgConnectorSettings = {
  integrationId: string;
  title: string;
  settings: Array<{ key: string; value: string | null; operations: string[] }>;
  /** Values stored for settings the connector no longer binds. */
  unused: string[];
};

export type AdminOrgFabric = {
  organizationId: string;
  placement: AdminOrgFabricPlacement | null;
  fabrics: AdminFabric[];
  defaultFabricId: string | null;
  provisioningStarted?: boolean;
};

export type AdminAgentEngine = 'omp' | 'opencode';

/** The coding agent new builds and chats run on when the request names none, and where that comes from. */
export type AdminDefaultAgentEngine = {
  engine: AdminAgentEngine;
  source: 'setting' | 'env' | 'default';
  /** The admin pick, or null when the server default applies. */
  setting: AdminAgentEngine | null;
  serverDefault: { engine: AdminAgentEngine; source: 'env' | 'default' };
  options: AdminAgentEngine[];
  updatedAt: string | null;
  updatedByUserId: string | null;
  updatedByEmail: string | null;
};

export type AdminAppliedPromo = {
  type: 'percent_off' | 'amount_off' | 'trial';
  percentOff?: number;
  amountOffCents?: number;
  code?: string;
  couponId?: string;
  name?: string;
  endsAt?: string;
};

export type AdminCreditEntry = {
  id: string;
  type: 'grant' | 'spend' | 'adjust';
  amountCents: number;
  reason: string;
  createdByUserId?: string;
  source?: string;
  sourceId?: string;
  /** Resolved from llm/integration usage → session → Foundry project when possible. */
  projectId?: string;
  projectTitle?: string;
  createdAt: string;
};

export type AdminOrgBilling = {
  organizationId: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  status?: string;
  priceId?: string;
  productName?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  creditBalanceCents?: number;
  creditBalanceUsd?: number;
  creditGrantedCents?: number;
  creditGrantedUsd?: number;
  creditSpentCents?: number;
  creditSpentUsd?: number;
  appliedPromo?: AdminAppliedPromo;
  pendingCouponId?: string;
  trialPeriodDays?: number;
  trialEnd?: string;
  grantedCents?: number;
  alreadyApplied?: boolean;
  couponId?: string;
  promoCode?: string;
  invoices: Array<{
    id: string;
    number?: string | null;
    status?: string | null;
    amountDue: number;
    amountPaid: number;
    currency: string;
    created: string;
    hostedInvoiceUrl?: string | null;
  }>;
};

export type AdminPriceRow = {
  priceId: string;
  productName: string;
  unitAmount: number | null;
  currency: string;
  interval?: string | null;
};

/** A Studio role's key from the API's registry (`studio-shared/constants/studioRoles`), e.g. `platform_admin`. */
export type StudioTeamRoleKey = string;

export type StudioTeamRoleGroup = { key: string; label: string };

/** A bundle (Internal Access, Developer Access, …) carries one or more permissions; Platform Admin is the one permission role. */
export type StudioTeamRoleKind = 'bundle' | 'permission';

export type StudioTeamRoleSummary = {
  key: StudioTeamRoleKey;
  kind: StudioTeamRoleKind;
  /** The role's name in WorkOS. */
  name: string;
  /** What the role grants on Studio's API. */
  permissions: string[];
  /** Short name on the page. */
  label: string;
  /** What it allows, in one line. */
  description: string;
  group: string;
  /** Holding it opens (some of) Studio Admin. */
  opensAdmin: boolean;
  /** Giving it asks first; removing any role always does. */
  confirmGrant: boolean;
  /** WorkOS has a role by this name. */
  available: boolean;
  holders: number;
};

export type StudioTeamPersonRole = {
  key: StudioTeamRoleKey;
  addedAt?: string;
  addedBy?: string;
  removable: boolean;
  removeBlockedReason?: 'self_lockout' | 'last_admin';
};

export type StudioTeamPerson = {
  userId: string;
  email?: string;
  name?: string;
  picture?: string;
  /** In one of RapidSOS's internal organizations. Only internal people can be given a role. */
  internal: boolean;
  isYou: boolean;
  roles: StudioTeamPersonRole[];
};

export type StudioTeamChange = {
  id?: string;
  /** `invited` and `invite_revoked` are a role promised with an invitation, or withdrawn with it. */
  action: 'granted' | 'removed' | 'invited' | 'invite_revoked';
  roleKey: StudioTeamRoleKey;
  roleName: string;
  targetUserId?: string;
  targetEmail?: string;
  targetName?: string;
  /** The internal organization an invitation was sent into. */
  organizationId?: string;
  /** A role promised with an invitation, given once they joined. */
  viaInvitation?: boolean;
  actor: string;
  actorName?: string;
  at: string;
};

/** Someone invited into a RapidSOS organization who has not joined yet. */
export type StudioTeamInvitation = {
  orgId: string;
  orgName: string;
  email: string;
  /** Studio roles given once they join. */
  roles: StudioTeamRoleKey[];
  invitationId?: string;
  invitedBy?: string;
  invitedAt?: string;
  expiresAt?: string;
  expired: boolean;
};

export type StudioTeamOrg = { id: string; name: string; memberCount?: number };

/** What the Users page needs before it lists anyone. People come a page at a time from `getTeamPeople`. */
/** A permission a Studio role can carry, by its name on the page ("Code Editor"). */
export type StudioTeamPermission = { slug: string; label: string };

export type StudioTeamAccessResponse = {
  roleGroups: StudioTeamRoleGroup[];
  roles: StudioTeamRoleSummary[];
  /** Every permission a role can carry, in display order. */
  permissions?: StudioTeamPermission[];
  /** The role new RapidSOS people get (Internal Access): preselected in Invite, and what "Give to everyone" hands out. */
  defaultRoleKey?: StudioTeamRoleKey;
  internalOrgCount: number;
  /** RapidSOS's internal organizations, the ones the page lists and people can be invited into. */
  orgs?: StudioTeamOrg[];
  /** People in no RapidSOS organization who hold a Studio role. */
  outsiderCount?: number;
  /** The latest few changes; the full log is `getTeamActivity`. */
  recent: StudioTeamChange[];
};

export type StudioTeamAccessFilter = 'access' | 'everyone';

export type StudioTeamPeopleQuery = {
  orgId: string;
  q?: string;
  /** "Has role" entries: role keys, or `perm:` and a permission. */
  roles?: StudioTeamRoleKey[];
  access: StudioTeamAccessFilter;
  page: number;
  pageSize: number;
  /** Read who holds which role from WorkOS again, after a change. */
  fresh?: boolean;
};

export type StudioTeamPeoplePage = {
  orgId: string;
  page: number;
  pageSize: number;
  /** People matching the search and filters. */
  total: number;
  /** The organization's people with a Studio role, and all of them, before search and filters. */
  counts: { access: number; everyone: number };
  /** People in the organization without the default role: who "Give … to everyone" reaches. */
  missingDefault: number;
  people: StudioTeamPerson[];
};

export type StudioTeamInvitationsPage = {
  orgId: string;
  page: number;
  pageSize: number;
  total: number;
  invitations: StudioTeamInvitation[];
};

export type StudioTeamActivityAction = StudioTeamChange['action'];

export type StudioTeamActivityQuery = {
  page: number;
  /** Studio user id or email of the person whose role changed. */
  person?: string;
  role?: StudioTeamRoleKey;
  action?: StudioTeamActivityAction;
  /** Whole days, `YYYY-MM-DD`; `to` is included. */
  from?: string;
  to?: string;
  q?: string;
};

export type StudioTeamActivityPage = {
  page: number;
  pageSize: number;
  total: number;
  entries: StudioTeamChange[];
};

/** Studio Admin → Users → one person. */
export type StudioPersonSummary = {
  person: { userId: string; email?: string; name?: string; picture?: string; isYou: boolean };
  organizations: Array<{ id: string; name: string }>;
  /** Their Studio roles; null when WorkOS could not say. */
  roles: StudioTeamRoleKey[] | null;
  roleNames: Record<string, string>;
  lastSignInAt: string | null;
  signInCount: number | null;
  memberSince: string | null;
  lastActiveAt: string | null;
  /** Who looked at this person's page or prompts. */
  viewedBy: Array<{ userId: string; email?: string; lastAt: string; views: number }>;
};

/** An organization a person belongs to, and their Studio seat there (null when they have none). */
export type StudioPersonOrganization = {
  id: string;
  name: string;
  /** One of RapidSOS's own organizations. */
  internal: boolean;
  seat: { role: AdminStudioSeatRole; active: boolean } | null;
};

export type StudioPersonActivityEntry = {
  id: string;
  /** `activity` was recorded; the others are history Studio already kept. */
  source: 'activity' | 'history' | 'project' | 'role' | 'support';
  type: string;
  summary: string;
  at: string;
  organizationId?: string;
  projectId?: string;
  projectTitle?: string;
  sessionId?: string;
  runId?: string;
  targetUserId?: string;
  metadata?: Record<string, string | number | boolean>;
  ip?: string;
  device?: string;
};

export type StudioPersonActivityQuery = {
  type?: string;
  projectId?: string;
  /** Whole days, `YYYY-MM-DD`; `to` is included. */
  from?: string;
  to?: string;
  q?: string;
};

export type StudioPersonActivityPage = { entries: StudioPersonActivityEntry[]; nextCursor?: string; types: string[] };

export type StudioPersonProject = {
  projectId: string;
  organizationId: string;
  organizationName: string;
  title: string;
  sessionId?: string;
  updatedAt?: string;
  createdAt?: string;
  versions: number;
  prompts: number;
  role: 'owner' | 'admin' | 'member' | 'contributor';
  thumbnailUrl?: string;
  deleted: boolean;
  /** Studio's read-only support view of this project, opened in a new tab. */
  openUrl?: string;
};

export type StudioPersonPrompt = {
  id: string;
  text: string;
  mode: string;
  at: string;
  sessionId?: string;
  runId?: string;
  runStatus?: string;
  version?: number;
  snapshotId?: string;
  attachments: number;
};

export type StudioPersonPromptsPage = {
  project: { projectId: string; organizationId: string; title: string; sessionId?: string; deleted: boolean };
  prompts: StudioPersonPrompt[];
  nextBefore?: string;
};

export type StudioPersonSignIn = {
  id: string;
  at: string;
  kind: 'sign_in' | 'failed' | 'sign_out' | 'token';
  code: string;
  result: 'success' | 'failure';
  app: string;
  organizationId?: string;
  organizationName?: string;
  ip?: string;
  device?: string;
  connection?: string;
  reason?: string;
  count?: number;
};

export type StudioPersonSignInsResult = {
  configured: boolean;
  entries: StudioPersonSignIn[];
  lastSignInAt?: string;
};

/** "Give Internal Access to everyone": who was given it, how many already had it, and who WorkOS refused. */
export type StudioTeamBulkGrantResult = {
  roleKey: StudioTeamRoleKey;
  orgId: string;
  given: string[];
  alreadyHad: number;
  failed: string[];
};

export type StudioTeamInviteResult =
  | { email: string; status: 'invited'; roles: StudioTeamRoleKey[]; expiresAt: string }
  /** Has a login but is not in the organization: offer to add them directly. */
  | { email: string; status: 'exists'; canAddDirectly: boolean }
  | { email: string; status: 'already_member'; rolesGiven: StudioTeamRoleKey[]; verified: boolean }
  | { email: string; status: 'failed'; error: string };

export type StudioTeamInviteInput = {
  emails: string[];
  orgId: string;
  roles: StudioTeamRoleKey[];
  /** Invite even people who already have a login. */
  sendToExisting?: boolean;
};

export type AdminStudioSeatRole = 'owner' | 'admin' | 'member';

export type AdminStudioSeat = {
  userId: string;
  displayName: string;
  email: string;
  picture: string;
  role: AdminStudioSeatRole;
  active: boolean;
};

export type AdminStudioPendingGrant = {
  email: string;
  role: AdminStudioSeatRole;
  createdByUserId?: string;
};

export type AdminStudioSeatsResponse = {
  /** One page of seats matching the search and role filter, owners first. */
  seats: AdminStudioSeat[];
  total: number;
  limit: number;
  offset: number;
  /** Every seat in the org, before search and role filter. */
  seatCount: number;
  roleCounts: Record<AdminStudioSeatRole, number>;
  pendingGrants: AdminStudioPendingGrant[];
};

export type AdminAppSharingScope = 'none' | 'all' | 'selected';

/** Which other organizations an organization may share its apps with, and the shares its apps have. */
/** One Studio feature for one organization (`GET /api/admin/organizations/:orgId/features`). */
export type AdminOrgFeature = {
  feature: string;
  label: string;
  description: string;
  /** What the organization has while nothing is set for it. */
  defaultEnabled: boolean;
  /** Off here is off for every organization on this deploy, whatever is set. */
  deployEnabled: boolean;
  /** What RapidSOS set for the organization; null is the default. */
  setting: boolean | null;
  /** On for the organization right now. */
  enabled: boolean;
  note: string | null;
  updatedAt: string | null;
  updatedByUserId: string | null;
};

export type AdminOrgFeatures = { organizationId: string; features: AdminOrgFeature[] };

export type AdminOrgAppSharing = {
  organizationId: string;
  scope: AdminAppSharingScope;
  /** The chosen organizations, for `selected`. */
  organizations: Array<{ organizationId: string; name: string }>;
  updatedAt?: string;
  updatedByUserId?: string;
  shares: Array<{
    projectId: string;
    title: string;
    organizationId: string;
    name: string;
    /** False once the rule no longer covers this organization: nobody gets in through it. */
    allowed: boolean;
    sharedAt?: string;
  }>;
};

/** Someone already in WorkOS who has no Studio seat in the organization yet. */
export type AdminStudioSeatCandidate = {
  userId: string;
  displayName: string;
  email: string;
  picture: string;
  /** False for someone found elsewhere in WorkOS: their seat joins them to the organization. */
  inOrganization: boolean;
};

/** Why a grant by email became an invitation instead of a seat. */
export type AdminStudioPendingReason = 'no_account' | 'not_verified' | 'ambiguous_account' | 'directory_unavailable';

export type AdminStudioSeatGrantResult = {
  seat?: AdminStudioSeat;
  /** True when the person was added to the organization to receive the seat. */
  joinedOrganization?: boolean;
  pendingGrant?: AdminStudioPendingGrant;
  reason?: AdminStudioPendingReason;
};

/** One person agreeing to one Terms of Use and Privacy Policy version pair. */
export type AdminLegalAcceptance = {
  userId: string;
  email: string | null;
  termsVersion: string;
  privacyVersion: string;
  acceptedAt: string;
  ip: string | null;
  userAgent: string | null;
};

export type AdminLegalAcceptancesResponse = {
  /** Whether Studio currently asks people to accept (`STUDIO_TERMS_ENFORCEMENT`). */
  enforced: boolean;
  items: AdminLegalAcceptance[];
};

export type DashboardOrgUsage = {
  llmCostUsd: number;
  integrationCostUsd: number;
  totalCostUsd: number;
  runs: number;
  inputTokens: number;
  outputTokens: number;
};

export type DashboardOrgBilling = {
  status?: string;
  productName?: string;
  stripeCustomerId?: string;
  creditBalanceUsd?: number;
  currentPeriodEnd?: string;
};

export type DashboardOrgMetrics = {
  organizationId: string;
  name: string;
  displayName: string;
  usage: DashboardOrgUsage;
  billing: DashboardOrgBilling;
};

export type DashboardTopIntegration = {
  integrationId: string;
  totalCostUsd: number;
  eventCount: number;
};

export type DashboardTrendDay = {
  date: string;
  llmCostUsd: number;
  integrationCostUsd: number;
  totalCostUsd: number;
  runs: number;
  activeOrgs: number;
  newApps: number;
};

export type DashboardTrendTotals = { totalCostUsd: number; runs: number; activeOrgs: number; newApps: number };

export type DashboardTrendsResponse = {
  from: string;
  to: string;
  days: DashboardTrendDay[];
  current: DashboardTrendTotals;
  previous: DashboardTrendTotals & { from: string; to: string };
};

export type DashboardMetricsResponse = {
  from: string;
  to: string;
  summary: {
    totalLlmCostUsd: number;
    totalIntegrationCostUsd: number;
    totalCostUsd: number;
    totalRuns: number;
    activeOrgsWithUsage: number;
    totalOrgs: number;
  };
  topIntegrations: DashboardTopIntegration[];
  organizations: DashboardOrgMetrics[];
};

export type AdminRunMarkerKey =
  | 'prompt.submitted'
  | 'plan.approved'
  | 'run.started'
  | 'seat.queued'
  | 'seat.claim_started'
  | 'seat.ready'
  | 'sandbox.ready'
  | 'agent.editing.start'
  | 'agent.start_context'
  | 'agent.resume_context'
  | 'llm.request_sent'
  | 'llm.first_token'
  | 'agent.first_write_started'
  | 'agent.first_tool'
  | 'agent.first_edit'
  | 'agent.editing.complete'
  | 'preview.ready'
  | 'run.failed';

export type AdminRunSegmentKey =
  | 'handoff'
  | 'handoff_and_seat'
  | 'seat_queue'
  | 'seat_claim'
  | 'agent_boot'
  | 'context_prep'
  | 'model_ttft'
  | 'first_token_to_tool'
  | 'model_and_agent'
  | 'explore_before_edit'
  | 'until_failure';

export type AdminRunMarker = {
  key: AdminRunMarkerKey;
  at: string;
  offsetMs: number | null;
  source: 'event' | 'derived' | 'stream';
  detail?: Record<string, string | number | boolean>;
};

export type AdminRunSegment = {
  key: AdminRunSegmentKey;
  fromKey: AdminRunMarkerKey;
  toKey: AdminRunMarkerKey;
  durationMs: number;
};

/** Mirrors `AdminRunTelemetry` in backend/api/src/lib/adminRunTelemetry.ts. */
export type AdminRunTelemetry = {
  anchor: { key: AdminRunMarkerKey; at: string } | null;
  model: {
    requested?: string;
    agent?: string;
    requestModel?: string;
    backend?: string;
    engine?: string;
    /** Why a run that asked for OMP ran on OpenCode (`guest_without_omp`). */
    engineFallback?: string;
    resolved: string[];
  };
  effort: { requested?: string; effective?: string };
  ttft: {
    ms?: number;
    source?: 'reported' | 'stream';
    baseline?: AdminRunMarkerKey;
    promptBytes?: number;
  };
  firstTool: {
    ms?: number;
    tool?: string;
    firstEditMs?: number;
    /** When the first write-type tool line arrived, before it finished (OMP) or with it (OpenCode). */
    firstWriteStartedMs?: number;
    firstWritePath?: string;
    sinceEditingStartMs?: number;
  };
  seat: {
    claimMs?: number;
    seat?: string;
    prewarmed?: boolean;
    workspaceReused?: boolean;
    workspaceRestored?: boolean;
    viteReadyMs?: number;
    appServiceReadyMs?: number;
    seatWaitMs?: number;
    queuedMs?: number;
  };
  startContext: {
    outcome?: string;
    bytes?: number;
    files?: number;
    durationMs?: number;
    seededFiles?: number;
    cacheHits?: number;
    cacheMisses?: number;
    cacheDiskHits?: number;
  } | null;
  resumeContext: { mode?: string; bytes?: number; savedBytes?: number } | null;
  usage: {
    costUsd?: number;
    turns?: number;
    durationMs?: number;
    inputTokens?: number;
    outputTokens?: number;
    cacheReadTokens?: number;
    metered?: boolean;
  };
  exploration: {
    readsBeforeEdit?: number;
    exploreLoopDetected?: boolean;
    maxConsecutiveReads?: number;
    totalReadTools?: number;
    totalWriteTools?: number;
  } | null;
  writeGate: {
    mode?: string;
    enforced?: boolean;
    ok?: boolean;
    reason?: string;
    firstTool?: string;
  } | null;
  /** OMP's own retries and compactions during the run; absent when it reported none. */
  omp?: { retries: number; compactions: number };
  markers: AdminRunMarker[];
  waitBreakdown: {
    fromKey: AdminRunMarkerKey;
    toKey: AdminRunMarkerKey;
    totalMs: number;
    segments: AdminRunSegment[];
  } | null;
  heartbeatCount: number;
};

export type AdminRunSummary = {
  runId: string;
  sessionId: string;
  projectId: string;
  status: string;
  mode?: string;
  attempt?: number;
  executorModel?: string;
  executorEffort?: string;
  agentEngine?: string;
  /** Set when the run asked for OMP and ran on OpenCode (`guest_without_omp`). */
  agentEngineFallback?: string;
  createdByUserId?: string;
  createdByName?: string;
  promptSnippet?: string;
  qualityRepair: boolean;
  error?: string;
  stats?: { additions?: number; deletions?: number; filesChanged?: number };
  createdAt?: string;
  updatedAt?: string;
};

export type AdminSessionSummary = {
  sessionId: string;
  projectId: string;
  title: string;
  status?: string;
  createdByUserId?: string;
  createdByName?: string;
  deleted: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type AdminSessionProjectRef = {
  projectId: string;
  title?: string;
  displayName?: string;
  packageName?: string;
  status?: string;
};

export type AdminRunFeedbackVote = {
  userId: string;
  displayName: string;
  email?: string;
  rating: 'up' | 'down';
  runId: string;
  sessionId: string;
  updatedAt: string;
};

export type AdminRunFeedback = {
  upCount: number;
  downCount: number;
  votes: AdminRunFeedbackVote[];
};

export type AdminSessionRunsResponse = {
  session: AdminSessionSummary;
  project: AdminSessionProjectRef | null;
  runs: Array<AdminRunSummary & { telemetry: AdminRunTelemetry; feedback?: AdminRunFeedback }>;
  truncated: boolean;
};

export type AdminRunEventRow = {
  id: string;
  type: string;
  key: string;
  at?: string;
  offsetMs: number | null;
  payload: Record<string, unknown>;
};

export type AdminRunDetailResponse = {
  run: AdminRunSummary & {
    prompt?: string;
    backendError?: string;
    appServiceError?: string;
    previewUrl?: string;
    workflowId?: string;
    budgets?: { maxTurns?: number; maxRuntimeMinutes?: number; maxRepairAttempts?: number };
  };
  session: AdminSessionSummary | null;
  project: AdminSessionProjectRef | null;
  telemetry: AdminRunTelemetry;
  /** Absent from an API that predates the execution trace. */
  trace?: AdminRunTrace | null;
  microvm?: AdminRunMicrovm | null;
  /** Absent from an API that predates per-run feedback on the run page. */
  feedback?: AdminRunFeedback;
  events: AdminRunEventRow[];
  eventsTruncated: boolean;
  streamLineCount: number;
};

export type AdminRunTraceSpan = {
  id: string;
  parentId?: string;
  label: string;
  startMs: number;
  endMs?: number;
  status: 'ok' | 'error' | 'open';
  attrs?: Record<string, string | number | boolean>;
};

export type AdminRunTrace = {
  origin: { key: string; at: string };
  endMs?: number;
  inProgress: boolean;
  spans: AdminRunTraceSpan[];
};

export type AdminRunMicrovm = {
  microvmId: string | null;
  imageVersion: string | null;
  lifecycle: Array<{ at: string; type: string; microvmId: string; runId?: string; imageVersion?: string; reason?: string; durationMs?: number }>;
};

export type IntegrationDraftStatus = 'draft' | 'in_review' | 'approved' | 'rejected' | 'published';
export type IntegrationLocale = 'en' | 'es' | 'cs' | 'ja';

export type IntegrationDraftOperation = {
  operationId: string;
  method: string;
  path: string;
  summary: string;
  tags: string[];
  kind: 'read' | 'write';
  enabled: boolean;
  /** The rate each call bills against; unset means free. */
  rateId?: string;
  /** Parameters the broker fills from an org setting or a fixed value, never from the app (proposal 0204). */
  bindings?: IntegrationDraftBinding[];
};

/** One pinned request parameter: `path` is one key per step, so a key may contain dots. */
export type IntegrationDraftBinding = {
  in: 'path' | 'query' | 'body';
  path: string[];
  setting?: string;
  value?: string | number | boolean | null;
};

export type IntegrationDraftContract = {
  specSha256: string;
  specFormat: 'json' | 'yaml';
  openapiVersion: string;
  title: string;
  version: string;
  servers: string[];
  baseUrl: string;
  securitySchemes: Array<{ name: string; type: string; in?: string; paramName?: string; scheme?: string }>;
  operations: IntegrationDraftOperation[];
  importedAt: string;
};

export type IntegrationDraftCopy = {
  title: string;
  headline: string;
  description: string;
  longDescription: string;
  capabilities: string[];
  agentGuidance: string;
  rateLabels: Record<string, string>;
};

export type IntegrationDraftRate = {
  id: string;
  providerRate: number;
  unit: string;
  billingModel: 'metered' | 'recurring' | 'per_action' | 'included';
  interval: 'request' | 'segment' | 'minute' | 'hour' | 'day' | 'month' | 'year';
  /** How many units `providerRate` buys: 1000 for "$1.50 per 1,000 lookups". */
  unitsPerPrice: number;
  freeTier?: number;
};

export type IntegrationDraftCredentialField = {
  key: string;
  label: string;
  description: string;
  exposure: 'server' | 'browser';
  required: boolean;
};

export type IntegrationDraftAuth = {
  kind: 'none' | 'api_key' | 'bearer' | 'basic' | 'oauth2' | 'caller_statement';
  in?: 'header' | 'query';
  paramName?: string;
  /** OAuth 2.0 grant; unset means authorization code (a person signs in). */
  grant?: 'authorization_code' | 'client_credentials';
  authorizationUrl?: string;
  tokenUrl?: string;
  scopes?: string[];
  scopeSeparator?: ' ' | ',';
  /** Client credentials: the API the token is for, when the token endpoint asks for one. */
  audience?: string;
  pkce?: boolean;
  /** How the client id and secret reach the token endpoint: `basic` header (default for client credentials) or the request `body`. */
  clientAuth?: 'body' | 'basic';
};

/** Whose account the connector calls with: one RapidSOS account, each organization's, or each signed-in user's. */
export type IntegrationConnectionScope = 'platform' | 'org' | 'user';

export type IntegrationDraftCheck = { id: string; label: string; ok: boolean; detail?: string; warning?: boolean };

export type IntegrationLogoConcept = {
  id: string;
  name: string;
  idea: string;
  /** A 64×64 SVG as a data URL, ready to store as the draft's logo. */
  dataUrl: string;
};

export type IntegrationDraft = {
  draftId: string;
  integrationId: string;
  status: IntegrationDraftStatus;
  category: string;
  capability: string;
  bindingScope: 'org' | 'user';
  connectionScope: IntegrationConnectionScope;
  /** Every way an admin may choose for it to sign in, the default (`connectionScope`) first. */
  authModes: IntegrationAuthMode[];
  /** Register this redirect URI with the service when it uses OAuth sign-in. */
  oauthRedirectUri: string;
  brandColor: string;
  logoDataUrl?: string;
  /** Published for `internal` testing by RapidSOS, or released to `everyone`. */
  audience?: 'internal' | 'everyone';
  /** Customers have a released version, which they keep while the team works on a new one. */
  hasRelease: boolean;
  /** A working draft of the live integration exists; the draft fields are the draft, `live` is what customers have. */
  hasDraftChanges: boolean;
  /** The working draft is published for RapidSOS testing: RapidSOS orgs get it in place of the live version. */
  testing: boolean;
  /** The version customers have, summarized. Present once there is one. */
  live?: IntegrationLiveSummary;
  /** How the working draft differs from the live version. */
  changes?: IntegrationDraftChange[];
  /** Changes that can make existing customer connections reconnect; applying them needs confirmation. */
  connectionChanges?: string[];
  /** Credentials with a value stored for the working draft only. */
  draftSecretKeys: string[];
  /**
   * What the integration's data is for an agent that calls it as a tool: each operation's role, where its records
   * are and which fields matter, the claim types it supports, guidance, a call budget. Studio stores it; the
   * work-host reads it (its schema: `backend/work-host/docs/data-description.md`).
   */
  dataDescription?: Record<string, unknown>;
  /** Set when it was made from a Studio app's API (Manage → API → Create integration). */
  appSource?: IntegrationAppSource;
  /** The app it was made from, on a single read. */
  app?: IntegrationSourceApp;
  editStartedAt?: string;
  editStartedBy?: string;
  releasedAt?: string;
  releasedBy?: string;
  releasedApprovedBy?: string;
  releasedApprovedAt?: string;
  poweredBy: string[];
  contract?: IntegrationDraftContract;
  copy: Partial<Record<IntegrationLocale, IntegrationDraftCopy>>;
  copyMeta: Partial<Record<IntegrationLocale, { source: 'human' | 'machine'; sourceHash?: string; translatedAt?: string }>>;
  keywords: string[];
  pricing: IntegrationDraftRate[];
  auth: IntegrationDraftAuth;
  credentials: IntegrationDraftCredentialField[];
  staleTranslations: IntegrationLocale[];
  capabilitySuggestions: string[];
  publishedAt?: string;
  publishedBy?: string;
  /** Presence only. The API never returns a secret value. */
  secrets: Array<{ key: string; updatedAt: string; updatedBy: string }>;
  review?: { decision: 'approved' | 'rejected'; note: string; reviewedBy: string; reviewedAt: string };
  checklist: IntegrationDraftCheck[];
  readyForReview: boolean;
  createdBy: string;
  updatedBy: string;
  submittedAt?: string;
  submittedBy?: string;
  createdAt: string;
  updatedAt: string;
};

export type IntegrationAppSource = { organizationId: string; projectId: string; sessionId: string; snapshotId: string };

/** The Studio app an integration was made from, and whether its owners offer it to everyone. */
export type IntegrationSourceApp = { name: string; appName: string; organizationId: string; offeredToEveryone: boolean; deleted: boolean };

export type IntegrationLiveSummary = {
  title: string;
  pricing: string;
  operationCount: number;
  connectionScope: IntegrationConnectionScope;
  authKind: IntegrationDraftAuth['kind'];
  releasedAt?: string;
  releasedBy?: string;
  approvedBy?: string;
  approvedAt?: string;
};

export type IntegrationDraftChange = { section: 'contract' | 'identity' | 'pricing' | 'copy' | 'connection'; text: string };

export type IntegrationActivityAction =
  | 'created'
  | 'draft_started'
  | 'edited'
  | 'submitted'
  | 'withdrawn'
  | 'approved'
  | 'rejected'
  | 'published_for_testing'
  | 'testing_stopped'
  | 'published'
  | 'applied'
  | 'released'
  | 'discarded'
  | 'retired'
  | 'deleted';

export type IntegrationActivityEntry = {
  action: IntegrationActivityAction;
  actor: string;
  actorName?: string;
  sections: string[];
  note?: string;
  details: string[];
  at: string;
};

export type IntegrationDraftSummary = Pick<
  IntegrationDraft,
  | 'draftId'
  | 'integrationId'
  | 'status'
  | 'audience'
  | 'hasRelease'
  | 'hasDraftChanges'
  | 'testing'
  | 'category'
  | 'brandColor'
  | 'logoDataUrl'
  | 'readyForReview'
  | 'updatedAt'
  | 'updatedBy'
> & { title: string; operationCount: number; app?: IntegrationSourceApp };

export type IntegrationDraftListQuery = { limit: number; offset: number; q?: string; status?: IntegrationDraftStatus };

/** A page of Integration Builder drafts; `statusCounts` counts the drafts matching the search, by status. */
export type IntegrationDraftListPage = {
  items: IntegrationDraftSummary[];
  total: number;
  limit: number;
  offset: number;
  statusCounts: Record<IntegrationDraftStatus, number>;
};

/** RapidSOS's account, the organization's, or each person's: what an admin may choose in Studio. */
export type IntegrationAuthMode = 'platform' | 'organization' | 'user';

export type IntegrationDraftUpdate = Partial<{
  category: string;
  capability: string;
  connectionScope: IntegrationConnectionScope;
  authModes: IntegrationAuthMode[];
  brandColor: string;
  logoDataUrl: string;
  poweredBy: string[];
  baseUrl: string;
  operations: Array<Pick<IntegrationDraftOperation, 'operationId' | 'kind' | 'enabled' | 'rateId' | 'bindings'>>;
  keywords: string[];
  copy: Partial<Record<IntegrationLocale, IntegrationDraftCopy>>;
  pricing: IntegrationDraftRate[];
  auth: IntegrationDraftAuth;
  credentials: IntegrationDraftCredentialField[];
  /** An object replaces the data description; null clears it. */
  dataDescription: Record<string, unknown> | null;
}>;

/** What "Generate with AI" filled in, and what the author should check. */
export type IntegrationDraftGeneration = {
  filled: string[];
  notes: string[];
  translation: { translated: string[]; failed: string[] };
  credentialsNeeded: string[];
};

export type IntegrationTestResult = { status: number; data: unknown; contentType: string; durationMs: number };

export type IntegrationDraftManifest = {
  manifest: Record<string, unknown>;
  locales: Partial<Record<IntegrationLocale, Record<string, unknown>>>;
};

function withQuery(
  path: string,
  input?: Record<string, string | number | undefined | null>,
): string {
  const params = new URLSearchParams();
  if (input) {
    for (const [key, value] of Object.entries(input)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

function withRange(
  path: string,
  input?: {
    from?: string;
    to?: string;
    projectId?: string;
    limit?: number;
    cursor?: string;
  },
): string {
  return withQuery(path, input);
}

export type StudioTemplateStatus = 'draft' | 'in_review' | 'approved' | 'rejected' | 'published' | 'retired';
export type StudioTemplateVersionStatus = 'pending_review' | 'approved' | 'rejected' | 'published' | 'superseded';
export type StudioTemplateVisibility = 'internal' | 'all_orgs' | 'org_list';
/** Who may see an item at all: `internal` needs Internal Access (or platform admin), usable only in RapidSOS organizations. */
export type StudioAudience = 'everyone' | 'internal';
export const STUDIO_TEMPLATE_CATEGORIES = ['Dispatch', 'Field operations', 'Public safety', 'Operations', 'Communications', 'Other'] as const;
export type StudioTemplateCategory = (typeof STUDIO_TEMPLATE_CATEGORIES)[number];

export type StudioTemplateReview = { decision: 'approved' | 'rejected'; note: string; reviewedBy: string; reviewedAt: string };

export type StudioTemplate = {
  templateId: string;
  slug: string;
  publisherOrgId: string;
  visibility: StudioTemplateVisibility;
  allowedOrgIds: string[];
  audience?: StudioAudience;
  sourceProjectId: string;
  sourceSessionId: string;
  status: StudioTemplateStatus;
  title: string;
  summary: string;
  description: string;
  category: StudioTemplateCategory;
  tags: string[];
  latestPublishedVersion?: number;
  usageCount: number;
  review?: StudioTemplateReview;
  submittedBy?: string;
  submittedAt?: string;
  publishedBy?: string;
  publishedAt?: string;
  retiredAt?: string;
  updatedBy: string;
  updatedAt: string;
};

/** A global skill (every organization can use it) managed in Studio Admin. */
export interface AdminSkill {
  skillId: string;
  scope: 'global' | 'org';
  name: string;
  description: string;
  body: string;
  enabled: boolean;
  audience?: StudioAudience;
  updatedAt?: string;
}

export type AdminSkillFilter = 'all' | 'published' | 'retired' | 'internal';

/** One page of global skills; `counts` are per filter, for the search. */
export type AdminSkillsPage = {
  skills: AdminSkill[];
  items: AdminSkill[];
  total: number;
  limit: number;
  offset: number;
  filter: AdminSkillFilter;
  counts: Record<AdminSkillFilter, number>;
};

export type AdminSkillInput = { name?: string; description?: string; body?: string; enabled?: boolean; audience?: StudioAudience };

export type StudioTemplateSummary = StudioTemplate & {
  pendingVersion: { version: number; submittedBy: string; submittedAt: string } | null;
};

/** `review`: a version waits for review. `draft`: not live yet. */
export type StudioTemplateListFilter = 'review' | 'live' | 'draft' | 'retired' | 'all';

export type StudioTemplateListQuery = { q?: string; status: StudioTemplateListFilter; limit: number; offset: number };

export type StudioTemplatePage = {
  items: StudioTemplateSummary[];
  total: number;
  limit: number;
  offset: number;
  /** How many match the search under each filter. */
  counts: Record<StudioTemplateListFilter, number>;
};

export type StudioTemplateVersion = {
  templateId: string;
  version: number;
  status: StudioTemplateVersionStatus;
  sourceSnapshotId: string;
  packageNameBase: string;
  needs: { integrations: Array<{ integrationId: string; capability?: string; evidence?: string }>; recordTypes: string[]; hasBackend: boolean };
  storage?: { archiveKey: string; previewPrefix?: string; copiedAt: string };
  changelog: string;
  submittedBy: string;
  submittedAt: string;
  review?: StudioTemplateReview;
  publishedAt?: string;
  publishedBy?: string;
  source: {
    snapshotId: string;
    sessionId: string;
    savedAt?: string;
    previewUrl?: string;
    thumbnailUrl?: string;
    quality: { status?: string; checks?: Array<{ id: string; status: 'passed' | 'failed' | 'unknown'; detail: string }> } | null;
  } | null;
};

export type StudioTemplateListingUpdate = Partial<
  Pick<StudioTemplate, 'title' | 'summary' | 'description' | 'category' | 'tags' | 'visibility' | 'allowedOrgIds' | 'audience'>
>;

/** A Studio app's API offered to every organization (Manage → API → Create integration), waiting on an approver. */
/** One support view: a person with Support Access looking at an organization's app on one day. */
export type AdminSupportAccess = {
  organizationId: string;
  sessionId: string | null;
  appTitle: string | null;
  supportUserId: string;
  supportEmail: string | null;
  supportName: string | null;
  day: string;
  firstSeenAt: string;
  lastSeenAt: string;
  requestCount: number;
};

export type AppIntegrationReviewStatus = 'pending' | 'approved' | 'rejected';

export type AppIntegrationReview = {
  integrationId: string;
  capability: string;
  name: string;
  description: string;
  organizationId: string;
  appName: string;
  operations: Array<{ method: string; path: string; summary: string; kind: 'read' | 'write' }>;
  approval: { status: AppIntegrationReviewStatus; requestedAt: string; requestedBy: string; reviewedBy?: string; reviewedAt?: string; note?: string } | null;
};

export type TokenHealthState = 'healthy' | 'stale' | 'failing' | 'needs_reconnect';

/** `GET /api/admin/connectors/token-health`. Mirrors the API's `TokenHealthResponse`. Never carries a token value. */
export type TokenHealthResponse = {
  generatedAt: string;
  refreshEveryDays: number;
  studio: {
    integrations: Array<{
      integrationId: string;
      title: string;
      counts: Record<TokenHealthState, number>;
      attention: Array<{
        state: 'failing' | 'needs_reconnect';
        scope: 'user' | 'org' | 'app';
        organizationId: string;
        organizationName?: string;
        personName?: string;
        personEmail?: string;
        projectName?: string;
        accountLabel?: string;
        lastRefreshedAt?: string;
        lastRefreshError?: string;
      }>;
      attentionOmitted: number;
    }>;
  };
};

export type AttentionSeverity = 'critical' | 'warning' | 'info';
export type AttentionArea = 'organizations' | 'integrations' | 'templates' | 'platform' | 'tokenHealth';

export type AttentionItem = {
  id: string;
  area: AttentionArea;
  severity: AttentionSeverity;
  title: string;
  detail: string;
  path: string;
  count?: number;
};

/** `GET /api/admin/attention`. Mirrors the API's `AttentionResponse`. */
export type AttentionResponse = {
  generatedAt: string;
  items: AttentionItem[];
  counts: Record<AttentionArea, number>;
  unavailable: AttentionArea[];
};

/** What the API holds for the caller: the token's permissions plus their Studio roles'. */
export type AdminMeResponse = {
  userId: string;
  permissions: string[];
  platformAdmin: boolean;
};

/** What a Studio Admin search hit is. */
export type AdminLookupKind = 'run' | 'session' | 'project' | 'organization' | 'person' | 'microvm';

/** One place a pasted id, organization name or person leads to (`GET /api/admin/lookup`). */
export type AdminLookupResult = {
  kind: AdminLookupKind;
  id: string;
  title: string;
  subtitle: string;
  orgId?: string;
  /** The Studio Admin route that opens it. */
  path: string;
};

export type AdminLookupResponse = { query: string; results: AdminLookupResult[] };

/** A run that failed in the last day, in any organization (`GET /api/admin/runs/failed`). */
export type AdminFailedRun = {
  runId: string;
  sessionId: string;
  projectId: string;
  organizationId: string;
  organizationName: string;
  projectName?: string;
  promptSnippet?: string;
  error?: string;
  createdAt: string;
  path: string;
};

export type AdminFailedRunsResponse = { since: string; truncated: boolean; items: AdminFailedRun[] };

export const adminClient = {
  async getMe(token: string): Promise<AdminMeResponse> {
    const response = await fetch(`${BASE_URL}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } });
    return handleResponse(response);
  },

  /** What needs a platform admin right now, worst first, and how many per area. */
  async getAttention(): Promise<AttentionResponse> {
    const response = await fetch(`${BASE_URL}/api/admin/attention`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async getDashboardMetrics(input?: {
    from?: string;
    to?: string;
    orgId?: string;
    includeZeroMetrics?: boolean;
  }): Promise<DashboardMetricsResponse> {
    const response = await fetch(
      withQuery(`${BASE_URL}/api/admin/dashboard/metrics`, {
        from: input?.from,
        to: input?.to,
        orgId: input?.orgId,
        includeZeroMetrics: input?.includeZeroMetrics ? 'true' : undefined,
      }),
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  /** Day-by-day usage, builds, active orgs and new apps, plus the previous window's totals. */
  async getDashboardTrends(input: { from: string; to: string; orgId?: string }): Promise<DashboardTrendsResponse> {
    const response = await fetch(withQuery(`${BASE_URL}/api/admin/dashboard/trends`, input), { headers: await authHeaders() });
    return handleResponse(response);
  },

  async listOrganizations(): Promise<{ items: AdminOrganizationRow[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/organizations`, {
      headers: await authHeaders(),
    });
    return handleResponse(response);
  },

  async getOrganization(orgId: string): Promise<AdminOrganizationRow> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async listProjects(
    orgId: string,
    input?: { limit?: number; offset?: number; q?: string; state?: AdminProjectState },
  ): Promise<{
    items: AdminProjectRow[];
    total: number;
    counts?: { active: number; deleted: number };
    limit: number;
    offset: number;
    hasMore: boolean;
  }> {
    const response = await fetch(
      withQuery(
        `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/projects`,
        input,
      ),
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async getProject(orgId: string, projectId: string): Promise<AdminProjectRow> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/projects/${encodeURIComponent(projectId)}`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async getOrgUsageSummary(
    orgId: string,
    input?: { from?: string; to?: string; projectId?: string },
  ): Promise<AdminUsageSummary> {
    const response = await fetch(
      withRange(
        `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/usage/summary`,
        input,
      ),
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async getOrgUsageEvents(
    orgId: string,
    input?: {
      from?: string;
      to?: string;
      limit?: number;
      cursor?: string;
      projectId?: string;
    },
  ): Promise<AdminUsageEventsResponse> {
    const response = await fetch(
      withRange(
        `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/usage/events`,
        input,
      ),
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async getSessionFeedback(
    orgId: string,
    sessionId: string,
  ): Promise<{
    sessionId: string;
    upCount: number;
    downCount: number;
    votes: Array<{
      userId: string;
      displayName: string;
      email?: string;
      rating: 'up' | 'down';
      runId: string;
      sessionId: string;
      updatedAt: string;
    }>;
  }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/sessions/${encodeURIComponent(sessionId)}/feedback`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  /** `engine` narrows the list to runs on one coding agent; omitted lists every run. */
  async getSessionRuns(orgId: string, sessionId: string, engine?: 'opencode' | 'omp'): Promise<AdminSessionRunsResponse> {
    const query = engine ? `?engine=${encodeURIComponent(engine)}` : '';
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/sessions/${encodeURIComponent(sessionId)}${query}`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async getRun(orgId: string, runId: string): Promise<AdminRunDetailResponse> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/runs/${encodeURIComponent(runId)}`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  /** What a pasted run, session, project, organization or MicroVM id is, or which organizations and RapidSOS people a name matches. */
  async lookup(query: string, signal?: AbortSignal): Promise<AdminLookupResponse> {
    const response = await fetch(`${BASE_URL}/api/admin/lookup?q=${encodeURIComponent(query)}`, {
      headers: await authHeaders(),
      signal,
    });
    return handleResponse(response);
  },

  /** Runs that failed in the last 24 hours across every organization, newest first. */
  async listFailedRuns(): Promise<AdminFailedRunsResponse> {
    const response = await fetch(`${BASE_URL}/api/admin/runs/failed`, { headers: await authHeaders() });
    return handleResponse(response);
  },



  async getProjectUsageSummary(
    orgId: string,
    projectId: string,
    input?: { from?: string; to?: string },
  ): Promise<AdminUsageSummary> {
    const response = await fetch(
      withRange(
        `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/projects/${encodeURIComponent(projectId)}/usage/summary`,
        input,
      ),
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async getProjectUsageEvents(
    orgId: string,
    projectId: string,
    input?: { from?: string; to?: string; limit?: number; cursor?: string },
  ): Promise<AdminUsageEventsResponse> {
    const response = await fetch(
      withRange(
        `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/projects/${encodeURIComponent(projectId)}/usage/events`,
        input,
      ),
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },


  async downloadOrgUsageActivityCsv(
    orgId: string,
    input?: { from?: string; to?: string; projectId?: string },
  ): Promise<Response> {
    const response = await fetch(
      withRange(
        `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/usage/events/export`,
        input,
      ),
      { headers: await authHeaders(false) },
    );
    if (!response.ok) await handleResponse(response);
    return response;
  },


  async downloadProjectUsageActivityCsv(
    orgId: string,
    projectId: string,
    input?: { from?: string; to?: string },
  ): Promise<Response> {
    const response = await fetch(
      withRange(
        `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/projects/${encodeURIComponent(projectId)}/usage/events/export`,
        input,
      ),
      { headers: await authHeaders(false) },
    );
    if (!response.ok) await handleResponse(response);
    return response;
  },



  async listFabrics(): Promise<{ environment: string; defaultFabricId: string | null; items: AdminFabricRow[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/fabrics`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async getFabricCapacity(): Promise<AdminFabricCapacity> {
    const response = await fetch(`${BASE_URL}/api/admin/fabrics/capacity`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async listFabricOrganizations(fabricId: string): Promise<{ fabricId: string; items: AdminOrgFabricPlacement[] }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/fabrics/${encodeURIComponent(fabricId)}/organizations`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async getOrgFeatures(orgId: string): Promise<AdminOrgFeatures> {
    const response = await fetch(`${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/features`, {
      headers: await authHeaders(),
    });
    return handleResponse(response);
  },

  async putOrgFeature(orgId: string, feature: string, input: { enabled: boolean | null; note?: string }): Promise<AdminOrgFeatures> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/features/${encodeURIComponent(feature)}`,
      { method: 'PUT', headers: await authHeaders(), body: JSON.stringify(input) },
    );
    return handleResponse(response);
  },

  async getOrgAppSharing(orgId: string): Promise<AdminOrgAppSharing> {
    const response = await fetch(`${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/app-sharing`, {
      headers: await authHeaders(),
    });
    return handleResponse(response);
  },

  async putOrgAppSharing(orgId: string, input: { scope: AdminAppSharingScope; organizationIds: string[] }): Promise<AdminOrgAppSharing> {
    const response = await fetch(`${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/app-sharing`, {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async getOrgFabric(orgId: string): Promise<AdminOrgFabric> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/fabric`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  /** Builder connectors this org can see that bind an org setting, with the org's values. */
  async listOrgConnectorSettings(orgId: string): Promise<{ organizationId: string; connectors: AdminOrgConnectorSettings[] }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/connector-settings`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  /** Replace the org's values for one connector; `null` forgets them, which stops its calls. */
  async putOrgConnectorSettings(orgId: string, integrationId: string, values: Record<string, string> | null): Promise<AdminOrgConnectorSettings> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/connector-settings/${encodeURIComponent(integrationId)}`,
      { method: 'PUT', headers: await authHeaders(), body: JSON.stringify({ values }) },
    );
    return handleResponse(response);
  },

  /** Place the org on a Fabric (the default when `fabricId` is omitted) and create its namespace; again to retry. */
  async putOrgFabric(orgId: string, input: { fabricId?: string | null }): Promise<AdminOrgFabric> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/fabric`,
      {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify(input),
      },
    );
    return handleResponse(response);
  },

  async getDefaultAgentEngine(): Promise<AdminDefaultAgentEngine> {
    const response = await fetch(`${BASE_URL}/api/admin/platform/default-agent-engine`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  /** Pick the coding agent new builds and chats run on; null goes back to the server default. */
  async putDefaultAgentEngine(engine: AdminAgentEngine | null): Promise<AdminDefaultAgentEngine> {
    const response = await fetch(`${BASE_URL}/api/admin/platform/default-agent-engine`, {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify({ engine }),
    });
    return handleResponse(response);
  },

  async listPrices(): Promise<{ items: AdminPriceRow[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/billing/prices`, {
      headers: await authHeaders(),
    });
    return handleResponse(response);
  },

  async getOrgBilling(orgId: string): Promise<AdminOrgBilling> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/billing`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async ensureCustomer(orgId: string): Promise<AdminOrgBilling> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/billing/ensure-customer`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({}),
      },
    );
    return handleResponse(response);
  },

  async createCheckout(
    orgId: string,
    priceId: string,
  ): Promise<{ url: string }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/billing/checkout`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ priceId }),
      },
    );
    return handleResponse(response);
  },

  async listOrgCreditEntries(
    orgId: string,
    input?: { limit?: number; offset?: number },
  ): Promise<{
    items: AdminCreditEntry[];
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  }> {
    const response = await fetch(
      withQuery(
        `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/billing/credits`,
        input,
      ),
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async grantOrgCredit(
    orgId: string,
    input: {
      amountUsd?: number;
      amountCents?: number;
      reason: string;
      idempotencyKey?: string;
    },
  ): Promise<AdminOrgBilling> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/billing/credits`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(input),
      },
    );
    return handleResponse(response);
  },

  async applyOrgPromo(
    orgId: string,
    input: {
      couponId?: string;
      percentOff?: number;
      amountOffCents?: number;
      duration?: 'once' | 'repeating' | 'forever';
      durationInMonths?: number;
      name?: string;
      generatePromoCode?: boolean;
      promoCode?: string;
      apply?: boolean;
      trialPeriodDays?: number;
    },
  ): Promise<AdminOrgBilling> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/billing/promo`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(input),
      },
    );
    return handleResponse(response);
  },

  async createPortal(orgId: string): Promise<{ url: string }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/billing/portal`,
      {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({}),
      },
    );
    return handleResponse(response);
  },

  /** Who from RapidSOS support opened this organization's apps in the read-only support view. */
  async listSupportAccess(orgId: string): Promise<{ access: AdminSupportAccess[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/support-access?organizationId=${encodeURIComponent(orgId)}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async listLegalAcceptances(orgId: string): Promise<AdminLegalAcceptancesResponse> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/legal-acceptances`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async listStudioSeats(
    orgId: string,
    input?: { limit?: number; offset?: number; q?: string; role?: AdminStudioSeatRole },
  ): Promise<AdminStudioSeatsResponse> {
    const response = await fetch(
      withQuery(`${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/studio-seats`, input),
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async searchWorkOSOrganizations(q: string): Promise<{ items: AdminWorkOSOrganization[]; total: number }> {
    const response = await fetch(`${BASE_URL}/api/admin/workos-organizations?q=${encodeURIComponent(q)}`, {
      headers: await authHeaders(),
    });
    return handleResponse(response);
  },

  async turnOnStudio(
    workosId: string,
    input: { ownerEmail?: string },
  ): Promise<{ organization: AdminWorkOSOrganization; owner?: AdminFirstOwner }> {
    const response = await fetch(`${BASE_URL}/api/admin/workos-organizations/${encodeURIComponent(workosId)}/studio`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async createStudioOrganization(input: {
    name: string;
    ownerEmail?: string;
  }): Promise<{ organization: AdminWorkOSOrganization; owner?: AdminFirstOwner }> {
    const response = await fetch(`${BASE_URL}/api/admin/organizations`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async turnOffStudio(orgId: string): Promise<{ organization: AdminWorkOSOrganization }> {
    const response = await fetch(`${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/studio`, {
      method: 'DELETE',
      headers: await authHeaders(false),
    });
    return handleResponse(response);
  },

  async grantMyStudioSeat(orgId: string, role: AdminStudioSeatRole = 'admin'): Promise<{ seat: AdminStudioSeat }> {
    const response = await fetch(`${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/studio-seats/me`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ role }),
    });
    return handleResponse(response);
  },

  async grantStudioSeat(
    orgId: string,
    input: { userId?: string; email?: string; role: AdminStudioSeatRole; joinOrganization?: boolean },
  ): Promise<AdminStudioSeatGrantResult> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/studio-seats`,
      { method: 'POST', headers: await authHeaders(), body: JSON.stringify(input) },
    );
    return handleResponse(response);
  },

  async listStudioSeatCandidates(orgId: string, q: string): Promise<{ candidates: AdminStudioSeatCandidate[]; total: number }> {
    const params = new URLSearchParams(q ? { q } : {});
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/studio-seats/candidates?${params}`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async revokeStudioSeat(orgId: string, userId: string): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/studio-seats/${encodeURIComponent(userId)}`,
      { method: 'DELETE', headers: await authHeaders(false) },
    );
    await handleResponse(response);
  },

  async withdrawStudioPendingGrant(orgId: string, email: string): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/admin/organizations/${encodeURIComponent(orgId)}/studio-seats/pending/${encodeURIComponent(email)}`,
      { method: 'DELETE', headers: await authHeaders(false) },
    );
    await handleResponse(response);
  },

  /** How every organization's connected accounts are doing, and who needs to reconnect. */
  async getTokenHealth(): Promise<TokenHealthResponse> {
    const response = await fetch(`${BASE_URL}/api/admin/connectors/token-health`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async getTeamAccess(): Promise<StudioTeamAccessResponse> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  /** One page of one internal organization's people, searched and filtered on the server. */
  async getTeamPeople(query: StudioTeamPeopleQuery): Promise<StudioTeamPeoplePage> {
    const params = new URLSearchParams({ orgId: query.orgId, access: query.access, page: String(query.page), pageSize: String(query.pageSize) });
    if (query.q) params.set('q', query.q);
    if (query.roles && query.roles.length > 0) params.set('role', query.roles.join(','));
    if (query.fresh) params.set('fresh', '1');
    const response = await fetch(`${BASE_URL}/api/admin/team-access/people?${params}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  /** People in no RapidSOS organization who hold a Studio role. */
  async getTeamOutsiders(): Promise<{ people: StudioTeamPerson[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/outsiders`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async getTeamInvitations(orgId: string, page: number): Promise<StudioTeamInvitationsPage> {
    const params = new URLSearchParams({ orgId, page: String(page) });
    const response = await fetch(`${BASE_URL}/api/admin/team-access/invitations?${params}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  /** One person's page: who they are, their roles and who has looked. Opening it is recorded. */
  async getPerson(userId: string): Promise<StudioPersonSummary> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/people/${encodeURIComponent(userId)}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  /** One page (50) of a person's activity, newest first. */
  async getPersonActivity(userId: string, params: URLSearchParams): Promise<StudioPersonActivityPage> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/people/${encodeURIComponent(userId)}/activity?${params}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async getPersonProjects(userId: string): Promise<{ projects: StudioPersonProject[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/people/${encodeURIComponent(userId)}/projects`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  /** A person's prompts in one project, newest first. Reading them is recorded. */
  async getPersonPrompts(userId: string, projectId: string, before?: string): Promise<StudioPersonPromptsPage> {
    const params = before ? `?${new URLSearchParams({ before })}` : '';
    const response = await fetch(
      `${BASE_URL}/api/admin/team-access/people/${encodeURIComponent(userId)}/projects/${encodeURIComponent(projectId)}/prompts${params}`,
      { headers: await authHeaders() },
    );
    return handleResponse(response);
  },

  async getPersonSignIns(userId: string): Promise<StudioPersonSignInsResult> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/people/${encodeURIComponent(userId)}/sign-ins`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async getPersonOrganizations(userId: string): Promise<{ organizations: StudioPersonOrganization[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/people/${encodeURIComponent(userId)}/organizations`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  /** The role change log, newest first, 25 a page. */
  async getTeamActivity(query: StudioTeamActivityQuery): Promise<StudioTeamActivityPage> {
    const params = new URLSearchParams({ page: String(query.page) });
    for (const key of ['person', 'role', 'action', 'from', 'to', 'q'] as const) {
      const value = query[key];
      if (value) params.set(key, value);
    }
    const response = await fetch(`${BASE_URL}/api/admin/team-access/activity?${params}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async grantTeamRole(roleKey: StudioTeamRoleKey, userId: string): Promise<{ alreadyHad?: boolean }> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/roles/${encodeURIComponent(roleKey)}/members`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ userId }),
    });
    return handleResponse(response);
  },

  /** Edit roles' Save: every change for one person in one call. */
  async setTeamPersonRoles(
    userId: string,
    change: { add: StudioTeamRoleKey[]; remove: StudioTeamRoleKey[] },
  ): Promise<{ added: StudioTeamRoleKey[]; removed: StudioTeamRoleKey[]; failed: Array<{ key: StudioTeamRoleKey; error: string; code: string }> }> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/people/${encodeURIComponent(userId)}/roles`, {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify(change),
    });
    return handleResponse(response);
  },

  /** Give the default role (Internal Access) to everyone in this RapidSOS organization who lacks it. */
  async grantDefaultTeamRoleToEveryone(orgId: string): Promise<StudioTeamBulkGrantResult> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/default-role/everyone`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({ orgId }),
    });
    return handleResponse(response);
  },

  async removeTeamRole(roleKey: StudioTeamRoleKey, userId: string): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/admin/team-access/roles/${encodeURIComponent(roleKey)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE', headers: await authHeaders(false) },
    );
    await handleResponse(response);
  },

  async inviteTeamMembers(input: StudioTeamInviteInput): Promise<{ results: StudioTeamInviteResult[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/invitations`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async addTeamMember(input: { email: string; orgId: string; roles: StudioTeamRoleKey[] }): Promise<{ rolesGiven: StudioTeamRoleKey[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/team-access/members`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async resendTeamInvitation(orgId: string, email: string): Promise<{ expiresAt: string }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/team-access/invitations/${encodeURIComponent(orgId)}/${encodeURIComponent(email)}/resend`,
      { method: 'POST', headers: await authHeaders(false) },
    );
    return handleResponse(response);
  },

  async revokeTeamInvitation(orgId: string, email: string): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/admin/team-access/invitations/${encodeURIComponent(orgId)}/${encodeURIComponent(email)}`,
      { method: 'DELETE', headers: await authHeaders(false) },
    );
    await handleResponse(response);
  },

  async listIntegrationDrafts(query: IntegrationDraftListQuery): Promise<IntegrationDraftListPage> {
    const params = new URLSearchParams({ limit: String(query.limit), offset: String(query.offset) });
    if (query.q) params.set('q', query.q);
    if (query.status) params.set('status', query.status);
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts?${params.toString()}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async createIntegrationDraft(input: { integrationId: string; title: string }): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async generateIntegrationDraft(input: {
    spec: string;
    description: string;
    title?: string;
    integrationId?: string;
  }): Promise<IntegrationDraft & { generation: IntegrationDraftGeneration }> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/generate`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async getIntegrationDraft(draftId: string): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}`, {
      headers: await authHeaders(),
    });
    return handleResponse(response);
  },

  async updateIntegrationDraft(draftId: string, update: IntegrationDraftUpdate): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}`, {
      method: 'PATCH',
      headers: await authHeaders(),
      body: JSON.stringify(update),
    });
    return handleResponse(response);
  },

  /** Logo and brand colour, in any status: a live integration's card updates without reopening it. */
  async updateIntegrationDraftBrand(draftId: string, update: { brandColor?: string; logoDataUrl?: string }): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/brand`, {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify(update),
    });
    return handleResponse(response);
  },

  async importIntegrationDraftSpec(draftId: string, spec: string): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/openapi`, {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify({ spec }),
    });
    return handleResponse(response);
  },

  /** Write-only: the value goes to the vault and is never sent back. */
  async setIntegrationDraftSecret(
    draftId: string,
    key: string,
    value: string,
  ): Promise<{ key: string; set: true; updatedAt: string; updatedBy: string }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/secrets/${encodeURIComponent(key)}`,
      { method: 'PUT', headers: await authHeaders(), body: JSON.stringify({ value }) },
    );
    return handleResponse(response);
  },

  async deleteIntegrationDraftSecret(draftId: string, key: string): Promise<void> {
    const response = await fetch(
      `${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/secrets/${encodeURIComponent(key)}`,
      { method: 'DELETE', headers: await authHeaders(false) },
    );
    await handleResponse(response);
  },

  async submitIntegrationDraft(draftId: string): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/submit`, {
      method: 'POST',
      headers: await authHeaders(),
      body: '{}',
    });
    return handleResponse(response);
  },

  /** App integrations offered to everyone from Manage → API, by approval status. Approvers only. */
  async listAppIntegrationReviews(status: AppIntegrationReviewStatus = 'pending'): Promise<{ items: AppIntegrationReview[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/app-integrations?status=${status}`, { headers: await authHeaders(false) });
    return handleResponse(response);
  },

  async reviewAppIntegration(integrationId: string, input: { decision: 'approved' | 'rejected'; note: string }): Promise<{ integrationId: string }> {
    const response = await fetch(`${BASE_URL}/api/admin/app-integrations/${encodeURIComponent(integrationId)}/review`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async reviewIntegrationDraft(
    draftId: string,
    input: { decision: 'approved' | 'rejected'; note: string },
  ): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/review`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async translateIntegrationDraft(
    draftId: string,
    locales?: IntegrationLocale[],
  ): Promise<IntegrationDraft & { translation: { translated: IntegrationLocale[]; failed: IntegrationLocale[] } }> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/translate`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(locales ? { locales } : {}),
    });
    return handleResponse(response);
  },

  /** Write the English copy, keywords and category with AI and translate them; draws a logo when there is none. Saved. */
  async writeIntegrationDraftCopy(draftId: string, description?: string): Promise<IntegrationDraft & { generation: { filled: string[]; notes: string[] } }> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/write-copy`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(description ? { description } : {}),
    });
    return handleResponse(response);
  },

  /** Three AI logo concepts for a draft; nothing is saved until the author picks one. */
  async generateIntegrationLogos(
    draftId: string,
    input: { brandColor?: string; category?: string; poweredBy?: string[]; direction?: string; avoid?: string[] },
  ): Promise<{ concepts: IntegrationLogoConcept[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/logo-concepts`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async setIntegrationDraftLifecycle(draftId: string, action: 'publish' | 'release' | 'unpublish' | 'reopen'): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/${action}`, {
      method: 'POST',
      headers: await authHeaders(),
      body: '{}',
    });
    return handleResponse(response);
  },

  /** Approvers only. `confirmReconnect` acknowledges that existing customer connections may need to reconnect. */
  async applyIntegrationDraft(draftId: string, input: { confirmReconnect?: boolean } = {}): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/apply`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async discardIntegrationDraft(draftId: string): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/discard`, {
      method: 'POST',
      headers: await authHeaders(),
      body: '{}',
    });
    return handleResponse(response);
  },

  async setIntegrationDraftTesting(draftId: string, testing: boolean): Promise<IntegrationDraft> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/testing`, {
      method: testing ? 'POST' : 'DELETE',
      headers: await authHeaders(testing),
      ...(testing ? { body: '{}' } : {}),
    });
    return handleResponse(response);
  },

  async getIntegrationDraftActivity(draftId: string): Promise<{ items: IntegrationActivityEntry[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/activity`, {
      headers: await authHeaders(),
    });
    return handleResponse(response);
  },

  async testIntegrationDraftOperation(
    draftId: string,
    input: {
      operationId: string;
      path?: Record<string, string>;
      query?: Record<string, string>;
      body?: unknown;
      idempotencyKey?: string;
      /** One-off values for this call only (an OAuth access token, an org's API key); never stored. */
      testCredentials?: Record<string, string>;
      /** Values for the operation's bound settings: no organization is calling a test. */
      testSettings?: Record<string, string>;
    },
  ): Promise<IntegrationTestResult> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/test`, {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async getIntegrationDraftManifest(draftId: string): Promise<IntegrationDraftManifest> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}/manifest`, {
      headers: await authHeaders(),
    });
    return handleResponse(response);
  },

  async deleteIntegrationDraft(draftId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/admin/integration-drafts/${encodeURIComponent(draftId)}`, {
      method: 'DELETE',
      headers: await authHeaders(false),
    });
    await handleResponse(response);
  },

  async listSkills(input?: { limit?: number; offset?: number; q?: string; filter?: AdminSkillFilter }): Promise<AdminSkillsPage> {
    const response = await fetch(withQuery(`${BASE_URL}/api/admin/skills`, input), { headers: await authHeaders(false) });
    return handleResponse(response);
  },

  async createSkill(input: AdminSkillInput): Promise<{ skill: AdminSkill }> {
    const response = await fetch(`${BASE_URL}/api/admin/skills`, { method: 'POST', headers: await authHeaders(), body: JSON.stringify(input) });
    return handleResponse(response);
  },

  /** A suggested draft or passage for a global skill being edited; nothing is saved. */
  async assistSkill(input: Record<string, string>): Promise<{ text: string }> {
    const response = await fetch(`${BASE_URL}/api/admin/skills/assist`, { method: 'POST', headers: await authHeaders(), body: JSON.stringify(input) });
    return handleResponse(response);
  },

  async updateSkill(skillId: string, input: AdminSkillInput): Promise<{ skill: AdminSkill }> {
    const response = await fetch(`${BASE_URL}/api/admin/skills/${encodeURIComponent(skillId)}`, {
      method: 'PATCH',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async deleteSkill(skillId: string): Promise<void> {
    const response = await fetch(`${BASE_URL}/api/admin/skills/${encodeURIComponent(skillId)}`, { method: 'DELETE', headers: await authHeaders(false) });
    await handleResponse(response);
  },

  async listStudioTemplates(query: StudioTemplateListQuery): Promise<StudioTemplatePage> {
    const params = new URLSearchParams({ status: query.status, limit: String(query.limit), offset: String(query.offset) });
    if (query.q) params.set('q', query.q);
    const response = await fetch(`${BASE_URL}/api/admin/studio-templates?${params}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async getStudioTemplate(templateId: string): Promise<{ template: StudioTemplate; versions: StudioTemplateVersion[] }> {
    const response = await fetch(`${BASE_URL}/api/admin/studio-templates/${encodeURIComponent(templateId)}`, { headers: await authHeaders() });
    return handleResponse(response);
  },

  async updateStudioTemplate(templateId: string, input: StudioTemplateListingUpdate): Promise<{ template: StudioTemplate }> {
    const response = await fetch(`${BASE_URL}/api/admin/studio-templates/${encodeURIComponent(templateId)}`, {
      method: 'PATCH',
      headers: await authHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse(response);
  },

  async reviewStudioTemplateVersion(
    templateId: string,
    version: number,
    input: { decision: 'approved' | 'rejected'; note: string },
  ): Promise<{ template: StudioTemplate }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/studio-templates/${encodeURIComponent(templateId)}/versions/${version}/review`,
      { method: 'POST', headers: await authHeaders(), body: JSON.stringify(input) },
    );
    return handleResponse(response);
  },

  async publishStudioTemplateVersion(templateId: string, version: number): Promise<{ template: StudioTemplate }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/studio-templates/${encodeURIComponent(templateId)}/versions/${version}/publish`,
      { method: 'POST', headers: await authHeaders(), body: '{}' },
    );
    return handleResponse(response);
  },

  async setStudioTemplateRetired(templateId: string, retired: boolean): Promise<{ template: StudioTemplate }> {
    const response = await fetch(
      `${BASE_URL}/api/admin/studio-templates/${encodeURIComponent(templateId)}/${retired ? 'retire' : 'unretire'}`,
      { method: 'POST', headers: await authHeaders(), body: '{}' },
    );
    return handleResponse(response);
  },
};
